import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Thermometer, Timer, Grip, Coffee, Scale, TrendingUp } from "lucide-react";
import ExtractionCalculator from "@/components/ExtractionCalculator";
import Seo from "@/components/Seo";

const DEFAULT_COFFEE_GRAMS = 15;
const DEFAULT_RATIO = 16;

export default function Calculator() {
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { data: methods = [] } = useQuery({ queryKey: ["methods"], queryFn: endpoints.methods });

  const [methodId, setMethodId] = useState("v60");
  const [mode, setMode] = useState("coffee"); // "coffee" -> input coffee g; "water" -> input water ml
  const [coffee, setCoffee] = useState(DEFAULT_COFFEE_GRAMS);
  const [water, setWater] = useState(DEFAULT_COFFEE_GRAMS * DEFAULT_RATIO);
  const [ratioOverride, setRatioOverride] = useState(null);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "extraction" ? "extraction" : "ratio";

  const selected = useMemo(
    () => methods.find((m) => m.id === methodId),
    [methods, methodId]
  );

  const ratio = ratioOverride ?? selected?.default_ratio ?? DEFAULT_RATIO;

  // Reset ratio override when method changes
  const handleMethodChange = (newId) => {
    setMethodId(newId);
    setRatioOverride(null);
  };

  const result = useMemo(() => {
    if (!selected || !ratio) return null;
    const ratioNum = Number(ratio);
    if (!ratioNum) return null;
    let coffeeNum, waterNum;
    if (mode === "water") {
      waterNum = Number(water);
      if (!waterNum) return null;
      coffeeNum = Math.round((waterNum / ratioNum) * 10) / 10;
    } else {
      coffeeNum = Number(coffee);
      if (!coffeeNum) return null;
      waterNum = Math.round(coffeeNum * ratioNum * 10) / 10;
    }
    return {
      method: selected.id,
      method_name_en: selected.name_en,
      method_name_ar: selected.name_ar,
      coffee_g: coffeeNum,
      water_g: waterNum,
      ratio: ratioNum,
    };
  }, [selected, coffee, water, ratio, mode]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="calculator-page">
      <Seo
        title={isRTL ? "حاسبة استخلاص القهوة TDS · EY · نسبة الريشيو" : "Coffee Extraction Calculator — TDS · EY · Ratio"}
        description={isRTL
          ? "حاسبة استخلاص القهوة الاحترافية: احسب TDS ونسبة الاستخلاص EY بدقة، وريشيو الإسبريسو، V60، الإيروبرس، الكيمكس وفق معايير SCA. أداة مجانية بالعربية لباريستا المختصة."
          : "Professional coffee extraction calculator: precise TDS & EY computation plus ratio helpers for espresso, V60, AeroPress and Chemex. SCA-aligned, free, in Arabic & English."}
        keywords="حاسبة استخلاص القهوة, حاسبة TDS, ما هو TDS في القهوة, كيف أحسب نسبة الاستخلاص في القهوة, معادلة الاستخلاص, نسبة الريشيو, ريشيو الإسبريسو, نسبة V60, EY قهوة, extraction yield, coffee TDS calculator, brewing ratio calculator"
        path="/calculator"
        jsonLdId="calc-jsonld"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": isRTL ? "حاسبة استخلاص القهوة" : "Coffee Extraction Calculator",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "url": "https://abdullah-coffee-science.academy/calculator",
          "description": isRTL ? "أداة لحساب TDS و EY ونسب التخمير للقهوة المختصة." : "Computes TDS, EY and brewing ratios for specialty coffee.",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "SAR" }
        }}
      />
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4">{isRTL ? "أداة" : "Tool"}</div>
        <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>{t.calculator.title}</h1>
        <p className="mt-6 text-lg text-[#5C544F] leading-relaxed">{t.calculator.lead}</p>
      </div>

      <Tabs defaultValue={initialTab} className="w-full mt-10">
        <TabsList className="bg-[#F9F6F0] border border-[#2A2421]/10 rounded-full p-1 h-auto inline-flex">
          <TabsTrigger value="ratio" className="rounded-full px-5 py-2 data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0]" data-testid="calc-tab-ratio">
            <Scale className="w-4 h-4 me-2" />
            {isRTL ? "حاسبة النسبة" : "Ratio calculator"}
          </TabsTrigger>
          <TabsTrigger value="extraction" className="rounded-full px-5 py-2 data-[state=active]:bg-[#C86240] data-[state=active]:text-white" data-testid="calc-tab-extraction">
            <TrendingUp className="w-4 h-4 me-2" />
            {isRTL ? "حاسبة الاستخلاص" : "Extraction calculator"}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ratio" className="mt-6">
          <CoffeeOfTheDayPresets onSelect={({ coffee, ratio }) => { setMode("coffee"); setCoffee(coffee); setRatioOverride(ratio); setMethodId("v60"); }} />
          <div className="grid lg:grid-cols-12 gap-8">
        {/* Inputs */}
        <div className="lg:col-span-5 soft-card rounded-2xl p-8" data-testid="calculator-inputs">
          <div className="space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{t.calculator.method}</Label>
              <Select value={methodId} onValueChange={handleMethodChange}>
                <SelectTrigger className="mt-2 h-12 rounded-xl border-[#2A2421]/15" data-testid="method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {methods.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`method-option-${m.id}`}>
                      {lang === "ar" ? m.name_ar : m.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">
                {isRTL ? "ابدأ من" : "Start from"}
              </Label>
              <div className="mt-2 inline-flex rounded-xl border border-[#2A2421]/15 overflow-hidden" role="tablist">
                <button
                  type="button"
                  data-testid="mode-coffee"
                  onClick={() => setMode("coffee")}
                  className={`px-4 py-2 text-sm transition-colors ${mode === "coffee" ? "bg-[#C86240] text-white" : "bg-white text-[#2A2421] hover:bg-[#F9F6F0]"}`}
                >
                  {isRTL ? "كمية البن" : "Coffee dose"}
                </button>
                <button
                  type="button"
                  data-testid="mode-water"
                  onClick={() => setMode("water")}
                  className={`px-4 py-2 text-sm transition-colors ${mode === "water" ? "bg-[#C86240] text-white" : "bg-white text-[#2A2421] hover:bg-[#F9F6F0]"}`}
                >
                  {isRTL ? "كمية الماء" : "Water amount"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {mode === "coffee" ? (
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{t.calculator.coffee}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={coffee}
                    onChange={(e) => setCoffee(e.target.value)}
                    className="mt-2 h-12 rounded-xl border-[#2A2421]/15 text-lg"
                    data-testid="coffee-input"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">
                    {isRTL ? "الماء (مل/جرام)" : "Water (ml / g)"}
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={water}
                    onChange={(e) => setWater(e.target.value)}
                    className="mt-2 h-12 rounded-xl border-[#2A2421]/15 text-lg"
                    data-testid="water-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{t.calculator.ratio}</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1"
                  value={ratio}
                  onChange={(e) => setRatioOverride(e.target.value)}
                  className="mt-2 h-12 rounded-xl border-[#2A2421]/15 text-lg"
                  data-testid="ratio-input"
                />
              </div>
            </div>

            {selected && (
              <div className="text-xs text-[#5C544F] pt-4 border-t border-[#2A2421]/10">
                {t.calculator.defaultRatio}: 1:{selected.default_ratio}
              </div>
            )}
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-7" data-testid="calculator-result">
          {result && selected && (
            <div className="soft-card rounded-2xl p-8 lg:p-10 h-full">
              <div className="flex items-baseline justify-between gap-4 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">{t.calculator.result}</div>
                  <h2 className={`${serifClass} text-3xl lg:text-4xl text-[#2A2421]`}>
                    {lang === "ar" ? result.method_name_ar : result.method_name_en}
                  </h2>
                </div>
                <div className="text-end">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F]">{t.calculator.ratio}</div>
                  <div className={`${serifClass} text-4xl text-[#C86240]`}>1:{result.ratio}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 my-8">
                <Big label={t.calculator.coffee} value={`${result.coffee_g}g`} testId="result-coffee" />
                <Big label={t.calculator.water} value={`${result.water_g}g`} testId="result-water" />
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <Mini icon={Grip} label={t.calculator.grind} value={lang === "ar" ? selected.grind_ar : selected.grind_en} />
                <Mini icon={Thermometer} label={t.calculator.temp} value={selected.temp} />
                <Mini icon={Timer} label={t.calculator.time} value={selected.time} />
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{t.calculator.steps}</div>
                <ol className="space-y-3" data-testid="brew-steps">
                  {(lang === "ar" ? selected.steps_ar : selected.steps_en).map((s, i) => (
                    <li key={`${selected.id}-${i}`} className="flex gap-4 text-[#2A2421] leading-relaxed">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-[#F9F6F0] border border-[#2A2421]/15 flex items-center justify-center text-xs font-display">
                        {i + 1}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>
          </div>
        </TabsContent>

        <TabsContent value="extraction" className="mt-6">
          <ExtractionCalculator />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Big = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F] mb-1">{label}</div>
    <div className="font-serif-en text-5xl lg:text-6xl text-[#2A2421] tracking-tight">{value}</div>
  </div>
);

const Mini = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-[#F9F6F0] p-4 border border-[#2A2421]/8">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#5C544F] mb-1.5">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="text-sm text-[#2A2421]">{value}</div>
  </div>
);

// Coffee of the Day — Batch-Brewer presets that load into the ratio calculator.
const COFFEE_OF_THE_DAY = [
  { id: "hot100",  label_en: "HOT 100",  label_ar: "100 ساخن",  coffee: 6,  ratio: 16.7, hint_en: "100 ml hot single cup",      hint_ar: "كوب ساخن 100 مل" },
  { id: "hot170",  label_en: "HOT 170",  label_ar: "170 ساخن",  coffee: 10, ratio: 17,   hint_en: "170 ml standard cup",       hint_ar: "كوب 170 مل" },
  { id: "hot240",  label_en: "HOT 240",  label_ar: "240 ساخن",  coffee: 14, ratio: 17.1, hint_en: "240 ml large cup",          hint_ar: "كوب كبير 240 مل" },
  { id: "batch500", label_en: "BATCH 500", label_ar: "دفعة 500",  coffee: 30, ratio: 16.7, hint_en: "500 ml batch brewer",       hint_ar: "Batch 500 مل" },
  { id: "batch1l", label_en: "BATCH 1 L", label_ar: "دفعة لتر",  coffee: 60, ratio: 16.7, hint_en: "1 L batch brewer",          hint_ar: "Batch 1 لتر" },
  { id: "cold260", label_en: "COLD 260", label_ar: "260 بارد",   coffee: 18, ratio: 14.4, hint_en: "260 ml iced (incl. ice)",   hint_ar: "260 مل مثلج (مع الثلج)" },
];

function CoffeeOfTheDayPresets({ onSelect }) {
  const { isRTL } = useI18n();
  return (
    <div className="mb-8 soft-card rounded-2xl p-6" data-testid="coffee-of-the-day-presets">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240]">{isRTL ? "قهوة اليوم" : "Coffee of the day"}</div>
          <div className="text-[#2A2421] text-sm mt-1">
            {isRTL ? "إعدادات سريعة للـ Batch Brewer — اضغط لتحميل الكمية والنسبة." : "Quick batch-brewer presets — tap to load the dose and ratio."}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {COFFEE_OF_THE_DAY.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect({ coffee: p.coffee, ratio: p.ratio })}
            data-testid={`cod-preset-${p.id}`}
            className="group rounded-xl border border-[#2A2421]/15 bg-white px-4 py-3 text-start hover:border-[#C86240] hover:bg-[#FDF4EC] transition-colors"
          >
            <div className="text-sm font-semibold text-[#2A2421] group-hover:text-[#C86240]">
              {isRTL ? p.label_ar : p.label_en}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C544F] mt-0.5">
              {p.coffee}g · 1:{p.ratio}
            </div>
            <div className="text-[10px] text-[#5C544F] mt-1">
              {isRTL ? p.hint_ar : p.hint_en}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
